import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Store, MapPin, Phone, Mail, Star, Package, 
  ArrowLeft, Loader2, Heart, ShoppingCart, ExternalLink
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import { useFavorites } from '../context/FavoritesContext';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartnerProfilePage = () => {
  const { country, partnerId } = useParams();
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addToCart } = useCart();
  
  const [partner, setPartner] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch partner profile from partners_profiles
        const partnerRes = await fetch(`${API_URL}/api/partners/${partnerId}`);
        if (partnerRes.ok) {
          const partnerData = await partnerRes.json();
          setPartner({
            id: partnerData.id,
            name: partnerData.name,
            slug: partnerData.slug,
            logo: partnerData.platform_logo_url || partnerData.logo_url || partnerData.logo,
            description: partnerData.about || partnerData.description,
            phone: partnerData.phone,
            email: partnerData.email,
            address: partnerData.address,
            city: partnerData.city,
            state: partnerData.state,
            whatsapp: partnerData.whatsapp,
            services: partnerData.services,
            business_hours: partnerData.business_hours,
            productsCount: partnerData.products_count || 0,
            averageRating: partnerData.average_rating || 0,
            totalReviews: partnerData.total_reviews || 0,
          });
        }
        
        // Fetch products from this partner
        const productsRes = await fetch(`${API_URL}/api/products?partner_id=${partnerId}&limit=100`);
        const productsData = await productsRes.json();
        setProducts(productsData);
        
        // Fallback: If no partner profile, build from product data
        if (!partnerRes.ok && productsData.length > 0) {
          setPartner({
            id: productsData[0].partner_id,
            name: productsData[0].partner_name,
            logo: productsData[0].partner_logo_url,
            productsCount: productsData.length,
            averageRating: productsData.reduce((sum, p) => sum + (p.average_rating || 0), 0) / productsData.length || 0,
            totalReviews: productsData.reduce((sum, p) => sum + (p.reviews_count || 0), 0)
          });
        }
      } catch (error) {
        console.error('Error fetching partner data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [partnerId]);

  const handleProductClick = (productId) => {
    navigate(`/${country}/products/${productId}`);
  };

  const handleToggleFavorite = (e, product) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-aug-cream flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-aug-blue" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen bg-aug-cream">
        <Header />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-2xl font-bold text-aug-gray-dark">Parceiro não encontrado</h1>
          <button
            onClick={() => navigate(`/${country}/products`)}
            className="mt-4 text-aug-blue hover:underline"
          >
            Voltar aos produtos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-[1400px] mx-auto px-xl">
          <Breadcrumbs
            items={[
              { label: 'Home', path: `/${country}` },
              { label: 'Produtos', path: `/${country}/products` },
              { label: partner.name, path: `/${country}/partner/${partnerId}` }
            ]}
          />

          {/* Partner Header */}
          <div className="bg-white rounded-2xl p-8 shadow-md mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              {/* Logo */}
              <div className="w-24 h-24 bg-gradient-to-br from-aug-blue to-aug-green rounded-2xl flex items-center justify-center shadow-lg">
                {partner.logo ? (
                  <img src={partner.logo} alt={partner.name} className="w-20 h-20 object-contain" />
                ) : (
                  <Store className="w-12 h-12 text-white" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-aug-gray-dark">
                    {partner.name}
                  </h1>
                  <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    Parceiro Verificado
                  </span>
                </div>
                
                <p className="text-aug-gray-medium mb-4">
                  Fornecedor oficial de produtos de climatização e refrigeração
                </p>

                <div className="flex flex-wrap items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    <span className="font-bold">{partner.averageRating.toFixed(1)}</span>
                    <span className="text-aug-gray-medium">({partner.totalReviews} avaliações)</span>
                  </div>
                  <div className="flex items-center gap-2 text-aug-gray-medium">
                    <Package className="w-5 h-5" />
                    <span><strong>{partner.productsCount}</strong> produtos disponíveis</span>
                  </div>
                </div>
              </div>

              {/* Contact Button */}
              <div className="flex flex-col gap-2">
                <button className="px-6 py-3 bg-aug-blue text-white rounded-xl font-medium hover:bg-aug-blue-dark transition-colors flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Entrar em Contato
                </button>
                {partner.slug && (
                  <a href={`/parceiros/${partner.slug}`} target="_blank" rel="noopener noreferrer"
                    className="px-6 py-3 bg-white border-2 border-aug-blue text-aug-blue rounded-xl font-medium hover:bg-aug-blue hover:text-white transition-colors flex items-center gap-2 justify-center"
                    data-testid="visit-partner-site-btn"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Visitar Site
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Products Section */}
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-aug-gray-dark">
                Produtos de {partner.name}
              </h2>
              <span className="text-aug-gray-medium">{products.length} produtos</span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductClick(product.id)}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  {/* Image */}
                  <div className="relative h-40 overflow-hidden bg-gray-100">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-xs">Sem imagem</span>
                      </div>
                    )}
                    
                    <button
                      onClick={(e) => handleToggleFavorite(e, product)}
                      className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg"
                    >
                      <Heart
                        className={`w-4 h-4 ${isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-400'}`}
                      />
                    </button>

                    {!product.in_stock && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                        <span className="bg-white text-gray-700 px-3 py-1 rounded-lg text-sm font-medium">
                          Esgotado
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="text-sm font-semibold text-aug-gray-dark line-clamp-2 mb-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    
                    <div className="flex items-center gap-1 mb-2">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-xs font-semibold">{product.average_rating || 0}</span>
                      <span className="text-xs text-aug-gray-medium">({product.reviews_count || 0})</span>
                    </div>

                    <div className="flex items-center justify-between">
                      <p className="text-lg font-bold" style={{ color: '#1B5E20' }}>
                        R$ {(product.sale_price || product.regular_price).toFixed(2)}
                      </p>
                      {product.in_stock && (
                        <span className="text-xs text-green-600 font-medium">Em estoque</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {products.length === 0 && (
              <div className="bg-white rounded-xl p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Nenhum produto encontrado
                </h3>
                <p className="text-gray-500">
                  Este parceiro ainda não possui produtos cadastrados.
                </p>
              </div>
            )}
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PartnerProfilePage;
